/**
 * Pattern Difficulty API client + shared TypeScript types.
 *
 * The backend computes hand-aware per-beat difficulty in
 * `quality_metrics.compute_pattern_difficulty`. This module exposes the
 * shape and a typed fetch helper.
 */

import { API_BASE } from './apiBase';

// ── Types (match backend dataclasses 1:1) ───────────────────────────────────

/** Per-beat metrics for a single hand stream (left / right / both). */
export interface HandMetrics {
  /** Per-beat hand speed in m/s (windowed average between same-hand notes). */
  speed: number[];
  /** Per-beat density in notes/sec for this hand stream (windowed). */
  density: number[];
  /** Per-beat reach 0..1 (avg distance between same-hand notes / arm span). */
  reach: number[];
  /** Per-beat stamina (rolling EMA of recent load), 0..1. */
  stamina: number[];
  /** Combined per-beat difficulty 0..1 for this hand. */
  difficulty: number[];

  peak_speed: number;
  avg_speed: number;
  peak_density: number;
  avg_density: number;
  peak_reach: number;
  note_count: number;
}

/** Full hand-aware difficulty report for a beatmap difficulty. */
export interface PatternDifficultyReport {
  left: HandMetrics;
  right: HandMetrics;
  /** Green notes (one_hand_special) — bound to one hand per run. */
  green: HandMetrics;
  /** Yellow notes (both_hands_special) — both arms at the same time. */
  both: HandMetrics;
  /** Aggregated per-beat curve 0..1 across all four streams. */
  combined: number[];
  /** 90th-percentile of `combined`, 0..1 — single overall difficulty score. */
  overall_score: number;
  /** Human-readable tier derived from overall_score. */
  rating: 'Easy' | 'Normal' | 'Hard' | 'Expert' | 'Master' | 'Insane';
  total_beats: number;
  bpm: number;
  arm_span_meters: number;
  grid_to_meters: number;
}

// ── Fetch ───────────────────────────────────────────────────────────────────

export interface FetchPatternDifficultyParams {
  beatmapId: string;
  difficulty: string;
  /** Player arm span in meters, measured from the loaded mannequin FBX. */
  armSpan?: number;
}

export async function fetchPatternDifficulty(
  params: FetchPatternDifficultyParams,
): Promise<PatternDifficultyReport> {
  const q = new URLSearchParams({
    difficulty: params.difficulty,
  });
  if (params.armSpan && params.armSpan > 0) {
    q.set('arm_span', params.armSpan.toFixed(3));
  }

  const url = `${API_BASE}/api/beatmaps/${encodeURIComponent(params.beatmapId)}/pattern-difficulty?${q.toString()}`;
  const res = await fetch(url);
  if (!res.ok) {
    let msg: string;
    try {
      const json = await res.json();
      msg = json.error || JSON.stringify(json);
    } catch {
      msg = await res.text().catch(() => `HTTP ${res.status}`);
    }
    throw new Error(`Pattern difficulty fetch failed: ${msg}`);
  }
  return res.json();
}

// ── Helpers ─────────────────────────────────────────────────────────────────

// Color ramp + contrast curve now live in @synapse/rhythm-core so the
// Guitar Hero editor can reuse the exact same visualisation. We re-export
// difficultyToColor so existing imports inside the SR editor keep working
// without touching every consumer.
export { difficultyToColor, contrastBoost, ratingFromScore } from '@synapse/rhythm-core';

/** Pill color for a rating string. */
export function ratingTone(rating: PatternDifficultyReport['rating']): {
  bg: string;
  fg: string;
} {
  switch (rating) {
    case 'Easy':
      return { bg: '#EAF3DE', fg: '#3B6D11' };
    case 'Normal':
      return { bg: '#E6F1FB', fg: '#0C447C' };
    case 'Hard':
      return { bg: '#FAEEDA', fg: '#854F0B' };
    case 'Expert':
      return { bg: '#FAECE7', fg: '#993C1D' };
    case 'Master':
      return { bg: '#FCEBEB', fg: '#A32D2D' };
    case 'Insane':
    default:
      return { bg: '#FBEAF0', fg: '#993556' };
  }
}
